import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    createInitialMiningState,
    updateTimer,
    processRoundEnd,
    deployToBlocks as deployToBlocksUtil,
    claimRewards as claimRewardsUtil,
} from '../utils/miningSimulation';
import { usePersistence } from '../hooks/usePersistence';

/**
 * Mining Context
 *
 * Provides mining state and functions to the entire app.
 * This context manages:
 * - Round timer
 * - Block deployments
 * - Reward calculations
 * - Round history
 *
 * Can be easily disabled or modified without affecting wallet or UI.
 */

const MiningContext = createContext(null);

export function MiningProvider({ children }) {
    const [miningState, setMiningState] = useState(createInitialMiningState);
    const [selectedBlocks, setSelectedBlocks] = useState(new Set());

    // Enable state persistence to localStorage
    usePersistence(miningState, setMiningState, createInitialMiningState(), {
        enabled: true,
        debounceMs: 2000, // Save 2 seconds after last change
    });

    /**
     * Timer effect - runs every second
     */
    useEffect(() => {
        const interval = setInterval(() => {
            setMiningState(prevState => {
                const { newState, roundEnded } = updateTimer(prevState);

                if (roundEnded) {
                    // Process round end
                    const { newState: finalState, roundResult } = processRoundEnd(newState);

                    // Log round result for debugging
                    if (roundResult) {
                        console.log('Round ended:', roundResult);
                    }

                    return finalState;
                }

                return newState;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    /**
     * Deploy SOL to selected blocks
     */
    const deployToBlocks = useCallback((blockNumbers, solPerBlock, numRounds = 1) => {
        setMiningState(prevState => {
            return deployToBlocksUtil(prevState, blockNumbers, solPerBlock * numRounds);
        });
    }, []);

    /**
     * Claim rewards
     */
    const claimRewards = useCallback((claimMINER = true, claimSOL = true) => {
        setMiningState(prevState => {
            const { newState, claimedMINER, claimedSOL, fee } = claimRewardsUtil(
                prevState,
                claimMINER,
                claimSOL
            );

            console.log('Claimed rewards:', { claimedMINER, claimedSOL, fee });

            return newState;
        });
    }, []);

    /**
     * Toggle block selection
     */
    const toggleBlockSelection = useCallback((blockNum) => {
        setSelectedBlocks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(blockNum)) {
                newSet.delete(blockNum);
            } else {
                newSet.add(blockNum);
            }
            return newSet;
        });
    }, []);

    /**
     * Clear block selection
     */
    const clearBlockSelection = useCallback(() => {
        setSelectedBlocks(new Set());
    }, []);

    /**
     * Reset mining state (for testing)
     */
    const resetMiningState = useCallback(() => {
        setMiningState(createInitialMiningState());
        setSelectedBlocks(new Set());
    }, []);

    const value = {
        // State
        miningState,
        selectedBlocks,

        // Actions
        deployToBlocks,
        claimRewards,
        toggleBlockSelection,
        clearBlockSelection,
        resetMiningState,

        // Computed values
        deployedBlocks: Object.keys(miningState.userDeployments).map(Number),
    };

    return (
        <MiningContext.Provider value={value}>
            {children}
        </MiningContext.Provider>
    );
}

/**
 * Hook to use mining context
 */
export function useMiningContext() {
    const context = useContext(MiningContext);
    if (!context) {
        throw new Error('useMiningContext must be used within MiningProvider');
    }
    return context;
}
